import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import AgeConfirmModal from "./components/AgeConfirmModal";
import LoginPage from "./pages/LoginPage";
import SignUpPage from "./pages/SignUpPage";
import AdminDashboard from "./pages/AdminDashboard";
import CategoriesPage from "./pages/CategoriesPage";
import ProvidersPage from "./pages/ProvidersPage";
import GamesPage from "./pages/GamesPage";
import PrivateRoute from "./components/PrivateRoute";
import HomePage from "./pages/HomePage";
import ProjectNamePage from "./pages/ProjectNamePage";
import GameUploadNewVersion from "./pages/GameUploadNewVersion";

const AGE_VERIFICATION_STORAGE_KEY = "ageVerifiedAt";
const AGE_VERIFICATION_TIMEOUT = 5 * 60 * 60 * 1000; // 5 ঘণ্টা = 18,000,000 ms


function App() {
  const [showAgeModal, setShowAgeModal] = useState(true);
  const ageTimerRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = showAgeModal ? "hidden" : "";
  }, [showAgeModal]);

  const scheduleAgeModalRecheck = (delay) => {
    if (ageTimerRef.current) {
      clearTimeout(ageTimerRef.current);
    }
    ageTimerRef.current = setTimeout(() => {
      setShowAgeModal(true);
      localStorage.removeItem(AGE_VERIFICATION_STORAGE_KEY);
    }, delay);
  };

  useEffect(() => {
    const saved = localStorage.getItem(AGE_VERIFICATION_STORAGE_KEY);
    if (!saved) {
      setShowAgeModal(true);
      return;
    }
    const elapsed = Date.now() - Number(saved);
    if (Number.isNaN(elapsed) || elapsed >= AGE_VERIFICATION_TIMEOUT) {
      localStorage.removeItem(AGE_VERIFICATION_STORAGE_KEY);
      setShowAgeModal(true);
      return;
    }
    setShowAgeModal(false);
    scheduleAgeModalRecheck(AGE_VERIFICATION_TIMEOUT - elapsed);
  }, []);

  useEffect(() => {
    return () => {
      if (ageTimerRef.current) {
        clearTimeout(ageTimerRef.current);
      }
    };
  }, []);

  // Auto logout for users after 30 minutes
  useEffect(() => {
    let logoutTimer;
    const checkUserAutoLogout = () => {
      const tokenStr = localStorage.getItem("token");
      if (tokenStr) {
        try {
          const token = JSON.parse(tokenStr);
          if (token.role === "user") {
            // Set timer for 30 minutes (1800000 ms)
            logoutTimer = setTimeout(() => {
              localStorage.removeItem("token");
              localStorage.removeItem("apiKey");
              window.location.href = "/login";
            }, 1800000);
          }
        } catch (e) {}
      }
    };
    checkUserAutoLogout();
    return () => {
      if (logoutTimer) clearTimeout(logoutTimer);
    };
  }, []);

  const handleAgeConfirm = (is18) => {
    if (is18) {
      setShowAgeModal(false);
      localStorage.setItem(
        AGE_VERIFICATION_STORAGE_KEY,
        String(Date.now())
      );
      scheduleAgeModalRecheck(AGE_VERIFICATION_TIMEOUT);
      return;
    }
    setShowAgeModal(true);
    localStorage.removeItem(AGE_VERIFICATION_STORAGE_KEY);
    alert("You must be 18+ to access Oracle Games.");
  };

  return (
    <>
      {showAgeModal && <AgeConfirmModal onConfirm={handleAgeConfirm} />}
      {!showAgeModal && (
        <Router>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/categories"
              element={
                <PrivateRoute>
                  <CategoriesPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/providers"
              element={
                <PrivateRoute>
                  <ProvidersPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/games"
              element={
                <PrivateRoute>
                  <GamesPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/game-upload-new-version"
              element={
                <PrivateRoute>
                  <GameUploadNewVersion />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/project-names"
              element={
                <PrivateRoute>
                  <ProjectNamePage />
                </PrivateRoute>
              }
            />
          </Routes>
        </Router>
      )}
    </>
  );
}

export default App;
